// Created: 2026-08-14
// Updated: 2026-09-08
//
// CRAFT ROOM query hooks (react-query-craft-room, Req 2, 3).
//
// Two hooks, two keys, two endpoints — the split itself is argued in `keys.ts` and is not re-derived
// here. Thin layer, same shape as slices 1–7: the transport stays in `src/api/fetchers/craftRoom.ts` and
// is not duplicated, and both `queryFn`s forward React Query's `signal` into their fetcher (Req 2.7,
// 12.3). That forward is load-bearing rather than decorative — it is what turns a sign-out or a navigation
// mid-load into a CANCELLATION rather than a query error.
//
// `useOwnCraftRoomQuery` has exactly one caller, `src/context/CraftRoomContext.tsx`, which turns the one
// entry into the provider's derived members. `useCraftRoomPageQuery` has exactly one caller,
// `src/components/pages/CraftRoom/CraftRoom.tsx`, on its visitor path.
//
// ─── THE TWO HOOKS ARE ASYMMETRIC ON PURPOSE ──────────────────────────────────────────────────────
//
// `useOwnCraftRoomQuery()` takes NO arguments and reads `user` / `screen_name` / `idToken` from
// `useAuth()` ITSELF (the slice-6 shape). `useCraftRoomPageQuery(screenName, { enabled })` takes both its
// screen name AND its `enabled` from the caller (the slice-7 shape). That is deliberate, and the reason is
// the same fact in both directions:
//
//   * The owner read's argument comes from the SESSION. There is exactly one value it can ever hold, no
//     caller could supply a better one, and pushing it through an argument would only give a caller the
//     opportunity to supply a worse one. Reading `useAuth()` here also means the provider holds no auth
//     plumbing beyond the ref its sign-out purge needs.
//   * The visitor read's gate is `!isOwner`, and **`isOwner` is the PAGE's derivation**
//     (`CraftRoom.tsx:47`, `screenName === screen_name`). Computing it again inside the query layer would
//     create a SECOND DEFINITION OF OWNERSHIP — two expressions that must agree forever, kept in agreement
//     by nobody in particular. So the page computes ownership once, keeps it as the selector between the
//     two reads, and passes the resulting boolean down (Req 3.4).
//
// The asymmetry is therefore not an inconsistency to tidy up: it tracks where each argument's truth lives.
//
// ─── A DISABLED QUERY KEEPS ITS DATA AND MUST NOT BLANK THE UI (Req 2.5) ──────────────────────────
//
// `enabled: false` does not evict, clear or reset an entry — React Query retains `data` and simply stops
// fetching. That property is what makes DROPPING the old public fallback strictly SAFER than the downgrade
// it replaces: the fallback arm *replaced* good owner data with a degraded public payload that forced
// `private_projects` and `archived_projects` to `[]` with no error, no log and no user-visible signal.
// `enabled: false` leaves the last good data in place and fetches nothing. (The provider still applies its
// own `enabled ? data : undefined` derive-guard on top, for the different reason that a PREVIOUS session's
// payload must not be repaintable — see `CraftRoomContext.tsx`.)
//
// ─── NO `staleTime` / `gcTime` / `refetchInterval` OVERRIDE (Req 2.9, 2.10) ───────────────────────
//
// `src/queries/queryClient.ts` sets 5 min / 10 min client-wide and neither hook overrides either, and
// neither polls. Slice 4 set a 30 s override and the reason it did is the reason these must not: its data
// converged only after an ASYNC INDEXING lag, so a shorter window was the only way to see the result of a
// write. The craft room has no such lag —
//
//   * the Owner_Entry's volatile contents are moved by THIS client's own writes (publish, delete,
//     visibility, progress, material edits), and those writes now INVALIDATE it (`craftProject/mutations.ts`),
//     so the refresh is event-driven and immediate rather than discovered on a timer;
//   * the Visitor_Entry's contents are moved by ANOTHER user, whose writes this client cannot observe at
//     any polling frequency — a shorter window would buy a fraction of a stranger's edit latency at the
//     cost of a request per interval per craft room viewed.
//
// For the visitor read the 5-minute baseline is also strictly FEWER requests than the behavior being
// replaced: the page's `useState` + effect re-fetched on every mount, and again whenever the owner
// context's `projects` array changed identity.
//
// ─── THERE IS NO `mutations.ts` IN THIS DIRECTORY, AND THAT IS A DECISION ─────────────────────────
//
// Req 1.1 makes the module CONDITIONAL, and the condition is not met. This slice has two writes —
// `refetchCraftRoom` and `updateMaterials` — and neither is a `useMutation`: neither performs a server
// call. `refetchCraftRoom` is `invalidateQueries(craftRoomKeys.me())` and `updateMaterials` is a
// `setQueryData(craftRoomKeys.me(), updater)`; both are operations on the CLIENT, and both stay provider
// members so that the nine existing `refetchCraftRoom()` call sites and the three material-grid writers
// keep working unedited. The craft-room-reaching invalidations that DO belong to server writes live with
// the writes that cause them, in `src/queries/craftProject/mutations.ts`. So the absence of a
// `mutations.ts` here is an argued absence, not an omission — do not add one to hold either write.

import type { CraftRoomPageResponse, SecureCraftRoomPageResponse } from '@mrb91002/shared-types'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { fetchCraftRoomPage, fetchSecureCraftRoomPage } from '../../api/fetchers/craftRoom'
import { useAuth } from '../../context/AuthContext'
import { isUsableScreenName } from '../../utils/screenName'

import { craftRoomKeys } from './keys'

/**
 * PERSONAL — the signed-in caller's OWN craft room. Key `['me','craftRoom']`.
 *
 * **This hook is why the owner's craft room now loads on mount** (D1, BL-025's frontend half). There is no
 * imperative trigger anywhere: the provider that calls it wraps `App`, so the Owner_Entry is populated for
 * the whole authenticated shell. The pre-migration loader was reachable only from a single route-gated
 * call site, which meant `getUserMaterialBySource` answered from an empty inventory for any session that
 * never visited `/craft-room` — and answered it TRUTHFULLY as far as the client knew, which is why the
 * defect drove a write (`📦 Add to Craft Room` on an already-owned material). Nothing here implements
 * that fix; it is a property of the primitive.
 *
 * The gate is all three conjuncts (Req 2.3): `!!user && !!screen_name && !!idToken`. Each is independently
 * necessary, and an absent `idToken` means the query is **DISABLED** — never "call unauthenticated" and
 * never "fall back to the public endpoint" (Req 4.2, and see `keys.ts` for why the fallback cannot share
 * this key). The `!` assertions inside `queryFn` are sound for exactly that reason: React Query does not
 * invoke `queryFn` while `enabled` is false, and both values are conjuncts of the gate.
 *
 * The payload is cached VERBATIM (Req 1.9) — no normalisation, no default-filling, no array concatenation
 * on the way in. Every coercion happens at derivation time in the provider, so there is exactly one copy
 * of the truth and it cannot disagree with itself between two consumers.
 */
export function useOwnCraftRoomQuery(): UseQueryResult<SecureCraftRoomPageResponse, Error> {
  const { user, screen_name, idToken } = useAuth()

  return useQuery<SecureCraftRoomPageResponse, Error>({
    queryKey: craftRoomKeys.me(),
    // `signal` forward — the abort-as-cancellation seam. Keep it.
    queryFn: ({ signal }) => fetchSecureCraftRoomPage(screen_name!, idToken!, { signal }),
    // The screen-name conjunct is `isUsableScreenName`, NOT `!!screen_name`, and that is the whole of
    // the fix for the observed `GET /api/secure/craft-room/null` (404, then RETRIED — see below) fired
    // during profile creation. `encodeURIComponent(null)` is the string `'null'`, and `!!'null'` is
    // true, so a stringified null satisfied a truthiness gate and reached the URL as a path segment.
    // The query is SKIPPED rather than the value defaulted: there is no correct craft room to ask for
    // before a screen name exists, and any default would ask for someone else's.
    //
    // Why it showed up more than once per attempt: `fetchSecureCraftRoomPage` throws a BARE `Error`
    // with no numeric `status`, so `shouldRetryQuery` cannot see the 4xx and retries it as though it
    // were a 5xx (that gap is recorded at the fetcher and is not this change's to close).
    enabled: !!user && isUsableScreenName(screen_name) && !!idToken,
  })
}

/**
 * PUBLIC — a craft room being VISITED. Key `['craftRoom', screenName]`.
 *
 * `screenName` comes from the route (`useParams()` via `CraftRoom.tsx`), so an absent value means the
 * query is disabled rather than that a request is made with `undefined` in the URL. `options.enabled` is
 * ANDed with that, and the page passes `!isOwner` — see the module header for why ownership is not
 * recomputed here.
 *
 * The two `enabled` conjuncts together are also what makes the "never invalidate the Visitor_Entry" rule
 * in `craftProject/mutations.ts` true rather than merely convenient: **an owner's own client never
 * populates `['craftRoom', ownScreenName]` in the first place**, because this hook is enabled only for a
 * non-owner. There is nothing there for a write to invalidate.
 *
 * Error behavior is preserved verbatim from the pre-migration visitor read (Req 3.7): on failure `data` is
 * `undefined`, so the page derives `craftRoom === null` and both arrays `[]` — an empty craft room with no
 * error banner, exactly what today's `catch` produced with its `console.error` and empty project list. The
 * diagnostic log is kept at the page's error path so the signal is not lost with the state. That this is
 * INDISTINGUISHABLE from a genuinely empty craft room is a known, recorded gap, not an intent.
 */
export function useCraftRoomPageQuery(
  screenName: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<CraftRoomPageResponse, Error> {
  return useQuery<CraftRoomPageResponse, Error>({
    queryKey: craftRoomKeys.page(screenName!),
    // `signal` forward — same seam as the owner read.
    queryFn: ({ signal }) => fetchCraftRoomPage(screenName!, { signal }),
    enabled: !!screenName && (options?.enabled ?? true),
  })
}
